export const adminGuideContent = `# Praxis — Admin & Architecture Guide

This guide covers the platform architecture, infrastructure layout, and the admin console for managing Praxis.

---

## Table of Contents

**Part 1: Architecture**
1. [Architecture Overview](#1-architecture-overview)
2. [Infrastructure Layout](#2-infrastructure-layout)
3. [Application Architecture](#3-application-architecture)
4. [Remote Desktop System](#4-remote-desktop-system)
5. [CI/CD Pipeline](#5-cicd-pipeline)
6. [Authentication Flow](#6-authentication-flow)
7. [Data Flow](#7-data-flow)

**Part 2: Admin Console**
8. [Admin Dashboard](#8-admin-dashboard)
9. [User Management](#9-user-management)
10. [Tech Eval Management](#10-tech-eval-management)
11. [Session Management](#11-session-management)
12. [Platform Settings](#12-platform-settings)
13. [Audit Log](#13-audit-log)
14. [Desktop Management](#14-desktop-management)

---

# Part 1: Architecture

## 1. Architecture Overview

\`\`\`
                            ┌─────────────────────────────────────────┐
                            │           praxis.cyberarklab.com        │
                            │              (CNAME → ALB)              │
                            └──────────────────┬──────────────────────┘
                                               │
                                               ▼
                            ┌─────────────────────────────────────────┐
                            │      Application Load Balancer (ALB)    │
                            │          workshophub-app-alb            │
                            │     HTTP:80 → redirect HTTPS:443       │
                            │                                         │
                            │  Path Routing:                          │
                            │    /api/*        → Backend (3001)       │
                            │    /socket.io/*  → Backend (3001)       │
                            │    /*            → Frontend (80)        │
                            └────────┬─────────────────┬──────────────┘
                                     │                 │
                              ┌──────▼──────┐   ┌──────▼──────┐
                              │   Backend   │   │  Frontend   │
                              │   (ECS)     │   │   (ECS)     │
                              │  NestJS     │   │ React+Nginx │
                              │  Port 3001  │   │  Port 80    │
                              │  0.5 vCPU   │   │  0.25 vCPU  │
                              │  1 GB RAM   │   │  512 MB RAM │
                              └──┬───┬───┬──┘   └─────────────┘
                                 │   │   │
                    ┌────────────┘   │   └────────────┐
                    ▼                ▼                 ▼
            ┌──────────────┐ ┌──────────────┐ ┌──────────────┐
            │  PostgreSQL  │ │ Guacamole EC2│ │   S3 Bucket  │
            │   (RDS)      │ │ 44.254.172.65│ │   (Media)    │
            │  Port 5432   │ │  Port 80     │ │  Presigned   │
            │  Private     │ │  VNC Proxy   │ │  URLs only   │
            └──────────────┘ └──────┬───────┘ └──────────────┘
                                    │
                                    ▼
                            ┌──────────────────┐
                            │  Kiosk Fleet     │
                            │  (EC2 ASG)       │
                            │  0-10 workers    │
                            │  25 desktops/ea  │
                            └──────────────────┘
\`\`\`

**Key Numbers:**
- **AWS Account:** 155941782025
- **Region:** us-west-2 (Oregon)
- **CDK Stacks:** 14 CloudFormation stacks
- **Domain:** praxis.cyberarklab.com (HTTPS via ACM)

---

## 2. Infrastructure Layout

### VPC & Networking

| Resource | Value |
|----------|-------|
| **VPC CIDR** | 10.0.0.0/16 |
| **Availability Zones** | us-west-2a, us-west-2b |
| **Public Subnets** | ALB, NAT Gateway |
| **Private Subnets** | RDS, ECS Tasks |
| **NAT Gateway** | 1x (single AZ, cost-optimized) |

### Compute — ECS Fargate

| Service | CPU | Memory | Port | Image |
|---------|-----|--------|------|-------|
| **Backend** | 0.5 vCPU | 1 GB | 3001 | workshophub-backend:latest |
| **Frontend** | 0.25 vCPU | 512 MB | 80 | workshophub-frontend:latest |

- **Cluster:** workshophub-cluster
- **Launch Type:** Fargate (serverless containers)
- **Circuit Breaker:** Enabled with rollback
- **Container Insights:** Enabled

### Database — RDS PostgreSQL

| Property | Value |
|----------|-------|
| **Engine** | PostgreSQL 15 |
| **Instance** | db.t4g.small (2 vCPU, 2 GB RAM, ARM) |
| **Storage** | 20 GB GP3, auto-scale to 100 GB |
| **Multi-AZ** | No (single AZ) |
| **Backups** | 7-day retention, 03:00-04:00 UTC |
| **Encryption** | At-rest (SSE) |
| **Monitoring** | Enhanced (60s), Performance Insights (7d) |

### Load Balancer — ALB

| Listener | Action |
|----------|--------|
| **HTTP :80** | 301 redirect to HTTPS |
| **HTTPS :443** | Path-based routing (see diagram above) |

**Certificate:** ACM — praxis.cyberarklab.com

**Routing Rules:**

| Priority | Path | Target |
|----------|------|--------|
| 10 | /api/* | Backend (3001) |
| 20 | /socket.io/* | Backend (3001) |
| Default | /* | Frontend (80) |

### Storage — S3

| Property | Value |
|----------|-------|
| **Bucket** | workshophub-media-155941782025 |
| **Access** | Private (presigned URLs only) |
| **Purpose** | Workshop images, videos, media |
| **Encryption** | SSE-S3 |

### Container Registry — ECR

| Repository | Purpose |
|------------|---------|
| workshophub-backend | Backend Docker images |
| workshophub-frontend | Frontend Docker images |
| workshophub-kiosk | Kiosk desktop browser images |

All repositories: scan-on-push enabled, keep last 10 images.

### Secrets Manager

| Secret | Purpose |
|--------|---------|
| workshop-hub/database | DB host, user, password |
| workshop-hub/jwt-secret | JWT signing key (64-char) |
| workshop-hub/guacamole-api | Guacamole API password |
| workshop-hub/kiosk-agent-api-key | Kiosk agent auth |
| workshop-hub/oidc-client-secret | CyberArk SSO client secret |

### Email — SES

| Property | Value |
|----------|-------|
| **Sender** | rishikeshrocz@gmail.com |
| **Config Set** | workshophub-emails |
| **Account Tier** | Production (50K/day) |
| **Purpose** | Session invitations, reminders |

### CloudWatch

| Log Group | Retention |
|-----------|-----------|
| /workshophub/backend | 14 days |
| /workshophub/frontend | 14 days |
| /aws/codebuild/workshophub-build | Default |

**Alarms:**
- RDS HighCPU (>80%)
- RDS LowStorage (<2 GB)
- RDS HighConnections (>80)

---

## 3. Application Architecture

### Backend (NestJS)

\`\`\`
backend/src/
├── modules/
│   ├── auth/          → JWT login, SSO/OIDC, role guards
│   ├── users/         → User CRUD
│   ├── workshops/     → Tech Eval content management
│   ├── steps/         → Step CRUD within tech evals
│   ├── sessions/      → Session lifecycle management
│   ├── students/      → Student enrollment, credentials
│   ├── progress/      → Step completion tracking
│   ├── help-requests/ → Help queue, messages, reactions
│   ├── guacamole/     → Remote desktop connection management
│   ├── media/         → S3 presigned URL generation
│   ├── kiosk-manager/ → Desktop fleet provisioning
│   ├── email/         → SES email service
│   ├── admin/         → Admin operations (users, workshops, sessions, settings, desktops, audit)
│   └── audit/         → Activity logging
├── gateways/
│   └── events.gateway.ts → WebSocket (Socket.IO) real-time events
└── main.ts            → App bootstrap (port 3001)
\`\`\`

**Key API Routes:**

| Prefix | Purpose |
|--------|---------|
| /api/auth | Login, register, SSO callback, profile |
| /api/workshops | Tech Eval CRUD |
| /api/workshops/:id/steps | Step management |
| /api/sessions | Session CRUD, lifecycle |
| /api/sessions/:id/students | Student enrollment |
| /api/sessions/:id/progress | Progress tracking |
| /api/sessions/:id/help-requests | Help queue |
| /api/media | Presigned URL generation |
| /api/admin | Admin operations |
| /api/kiosk | Desktop provisioning |

### Frontend (React 18 + MUI)

\`\`\`
frontend/src/
├── pages/
│   ├── auth/          → Login, SSO callback
│   ├── student/       → Dashboard, WorkshopView (2156 lines)
│   ├── instructor/    → Dashboard, Editor, Sessions, Live Console,
│   │                    Present Mode, Help Queue, Summary
│   └── admin/         → Dashboard, Users, Tech Evals, Sessions,
│                        Settings, Audit, Desktops
├── components/
│   ├── layout/        → AppShell, UserMenu, Sidebar
│   ├── workshop/      → Block editors and renderers
│   └── common/        → ProtectedRoute, LoadingSpinner
├── services/          → API client, WebSocket, Guacamole
├── context/           → AuthContext, NotificationContext
├── hooks/             → useAuth, useNotification
└── types/             → TypeScript interfaces
\`\`\`

### Database Schema (Key Entities)

\`\`\`
┌──────────┐    ┌──────────────┐    ┌──────────┐
│  Users   │───<│  Workshops   │───<│  Steps   │
│          │    │ (Tech Evals)│    │          │
│ id       │    │ id           │    │ id       │
│ email    │    │ title        │    │ title    │
│ role     │    │ status       │    │ type     │
│ password │    │ createdBy    │    │ blocks   │
└────┬─────┘    └──────┬───────┘    │ order    │
     │                 │            └──────────┘
     │                 │
     │          ┌──────▼───────┐
     │          │   Sessions   │
     │          │              │
     │          │ id           │
     │          │ name         │
     │          │ status       │
     │          │ workshopId   │
     │          │ instructorId │
     │          └──────┬───────┘
     │                 │
     │          ┌──────▼───────┐    ┌──────────────┐
     └─────────>│  Students    │───>│   Progress   │
                │ (enrollment) │    │              │
                │ sessionId    │    │ studentId    │
                │ userId       │    │ stepId       │
                │ credentials  │    │ status       │
                └──────┬───────┘    └──────────────┘
                       │
                ┌──────▼───────┐    ┌──────────────┐
                │ HelpRequests │───<│   Messages   │
                │              │    │              │
                │ studentId    │    │ helpReqId    │
                │ sessionId    │    │ userId       │
                │ status       │    │ text         │
                │ priority     │    │ reactions    │
                └──────────────┘    └──────────────┘
\`\`\`

---

## 4. Remote Desktop System

\`\`\`
Student Browser                    AWS Infrastructure
┌─────────────┐                   ┌─────────────────────────────────┐
│             │                   │                                 │
│  Guacamole  │  ◄── WebSocket ──│  Guacamole Server (EC2)        │
│  iframe     │      (ALB)       │  44.254.172.65                  │
│             │                   │                                 │
│             │                   │  ┌────────┐  ┌──────────────┐  │
│             │                   │  │ guacd  │──│ guacamole    │  │
└─────────────┘                   │  │ (proxy)│  │ (web UI)     │  │
                                  │  └───┬────┘  └──────────────┘  │
                                  │      │ VNC                     │
                                  └──────┼─────────────────────────┘
                                         │
                              ┌──────────▼─────────────────────┐
                              │     Kiosk Fleet Workers        │
                              │     (EC2 Auto Scaling Group)   │
                              │                                │
                              │  ┌────────────────────────┐    │
                              │  │ Worker Instance        │    │
                              │  │ t3.xlarge (16 GB RAM)  │    │
                              │  │                        │    │
                              │  │  ┌─────┐ ┌─────┐      │    │
                              │  │  │VNC 1│ │VNC 2│ ...   │    │
                              │  │  │:5901│ │:5902│ :5925 │    │
                              │  │  └─────┘ └─────┘       │    │
                              │  │  25 desktops/worker    │    │
                              │  │                        │    │
                              │  │  Kiosk Agent (:3002)   │    │
                              │  └────────────────────────┘    │
                              │                                │
                              │  Min: 0 / Max: 10 instances    │
                              └────────────────────────────────┘
\`\`\`

### Desktop Container

Each student desktop is an isolated Docker container running:
- **Xvfb** — Virtual display (1920x1080)
- **Openbox** — Window manager (no close/minimize)
- **Firefox ESR** — Pre-configured browser
- **x11vnc** — VNC server (random password per container)

### Scaling

| Trigger | Action | Cooldown |
|---------|--------|----------|
| Available slots < 10 (2 min) | Scale out +1 worker | 5 min |
| Active containers = 0 (30 min) | Scale in -1 worker | 5 min |
| Business hours (6 AM PT, Mon-Fri) | Set desired = 1 | — |
| Off-hours (8 PM PT, Mon-Fri) | Set desired = 0 | — |

### Fleet Coordination

- **DynamoDB Table:** workshophub-kiosk-fleet
- Workers register via heartbeat (every 60s)
- Backend queries DynamoDB to find worker with capacity
- Fallback: central EC2 if no fleet workers available

---

## 5. CI/CD Pipeline

\`\`\`
CodeCommit (develop)  →  CodeBuild  →  ECR  →  ECS Deploy
      │                     │                      │
      │                     ├─ Install deps         ├─ Backend service
      │                     ├─ Run 306 tests        └─ Frontend service
      │                     ├─ Docker build
      │                     └─ Docker push
      │
      └── Trigger: push to develop branch
\`\`\`

| Stage | What Happens | Duration |
|-------|-------------|----------|
| **Source** | Pull from CodeCommit develop branch | ~5s |
| **Install** | npm ci for backend + frontend | ~70s |
| **Pre-Build** | ECR login, run all tests (306 tests) | ~50s |
| **Build** | Docker build + tag both images | ~230s |
| **Post-Build** | Push images to ECR | ~40s |
| **Deploy** | Update ECS services (rolling) | ~3 min |

**Total pipeline time:** ~8-10 minutes

**Test Gate:** If any of the 306 tests fail, the build stops. No broken image reaches ECR or ECS.

---

## 6. Authentication Flow

### SSO Login (Instructors/Admins)

\`\`\`
Browser → /api/auth/oidc → CyberArk Identity (OIDC)
    ↓                              ↓
Redirect to IDP          Authenticate with corporate creds
    ↓                              ↓
Callback → /api/auth/oidc/callback
    ↓
Extract groups → mapGroupsToRole()
    ↓
    ├── workshophub-admin group  → ADMIN role
    ├── workshophub-instructor   → INSTRUCTOR role
    └── (default)                → INSTRUCTOR role
    ↓
Generate JWT → Redirect to /sso/callback?token=...
    ↓
Frontend stores JWT → Navigate to role-based dashboard
\`\`\`

### Local Login (Students)

\`\`\`
Instructor creates session → Assigns students with credentials
    ↓
SES sends invitation email → Student receives email + password
    ↓
Student → /login → POST /api/auth/login
    ↓
Validate credentials → Generate JWT → Return to frontend
    ↓
Frontend stores JWT → Navigate to /student
\`\`\`

---

## 7. Data Flow

### Real-Time Updates (WebSocket)

\`\`\`
Student marks step complete
    ↓
POST /api/sessions/:id/progress/:stepId
    ↓
Backend updates DB → Emits Socket.IO event
    ↓
    ├── Instructor Live Console receives update
    ├── Instructor Present Mode PiP receives update
    └── Student progress bar updates
\`\`\`

### Help Request Flow

\`\`\`
Student clicks "Request Help"
    ↓
POST /api/sessions/:id/help-requests
    ↓
Backend creates record → Emits Socket.IO event
    ↓
Instructor receives notification
    ↓
Instructor opens Help Queue → Types response
    ↓
POST /api/help-requests/:id/messages
    ↓
Student receives message in real-time
\`\`\`

### Desktop Provisioning Flow

\`\`\`
Instructor clicks "Prepare Session"
    ↓
Backend → Query DynamoDB for available worker
    ↓
POST worker:3002/provision (count = student count)
    ↓
Worker creates Docker containers (Firefox + VNC)
    ↓
Worker registers connections in Guacamole via REST API
    ↓
Backend stores connectionId per student
    ↓
Student joins session → GET /api/sessions/:id/desktop
    ↓
Backend returns Guacamole embed URL
    ↓
Frontend renders iframe with remote desktop
\`\`\`

---

# Part 2: Admin Console

## 8. Admin Dashboard

The dashboard provides a high-level overview of the platform.

### Stats Cards

| Metric | Description |
|--------|-------------|
| **Total Users** | All registered users |
| **Instructors** | Users with instructor role |
| **Students** | Users with student role |
| **Evaluations** | Total tech eval content |
| **Sessions** | Total sessions created |
| **Active Now** | Currently running sessions (pulsing indicator) |

### Charts

- **User Registrations** — 30-day trend (area chart)
- **Sessions Created** — 30-day trend (area chart)

### Active Sessions

Live cards for each running session showing:
- Workshop title and instructor name
- Student count and elapsed time
- Green pulsing indicator

### System Health

| Indicator | What It Shows |
|-----------|---------------|
| **Database** | Healthy/Unhealthy + response time (ms) |
| **Uptime** | Application uptime |
| **Memory** | Usage in MB |
| **Version** | Platform version number |

---

## 9. User Management

**Path:** Admin > Users

### User Table

Searchable and filterable list of all users with columns:
- User (avatar + name)
- Email
- Role (color-coded chip)
- Status (active/inactive indicator)
- Registered (when)
- Last Login (when, or "Never")

**Filters:**
- Search by name or email
- Filter by role: All, Admin, Instructor, Student
- Filter by status: All, Active, Inactive

### Actions

| Action | Description |
|--------|-------------|
| **Create User** | Add a new user with name, email, role, optional password (auto-generated if blank) |
| **Edit User** | Modify name, email, or role |
| **Reset Password** | Generate random or set specific password |
| **Deactivate** | Prevent user from logging in (reversible) |
| **Reactivate** | Re-enable a deactivated user |
| **Delete** | Permanently remove (requires typing the user's email to confirm) |
| **Bulk Import** | Upload CSV to create multiple users at once |

### Bulk Import

1. Upload a CSV file (or paste CSV data)
2. Preview imported rows — validation highlights errors (invalid email, missing name, duplicates)
3. Confirm import — created users shown with temporary passwords
4. Download passwords CSV for distribution

**CSV Format:** Name, Email, Role (optional)

### Self-Protection

You cannot deactivate or delete your own account.

---

## 10. Tech Eval Management

**Path:** Admin > Tech Evals

### Tech Eval Table

| Column | Description |
|--------|-------------|
| **Title** | Tech Eval name |
| **Instructor** | Who created it |
| **Status** | Draft / Published / Archived |
| **Steps** | Step count |
| **Sessions** | Number of sessions using it |
| **Duration** | Estimated total time |
| **Created** | When it was created |

**Filters:** Search by title, filter by status, filter by instructor

### Actions

| Action | Description |
|--------|-------------|
| **View Detail** | See full tech eval info, steps, sessions |
| **Transfer Ownership** | Reassign to a different instructor (also transfers sessions) |
| **Change Status** | Move between Draft / Published / Archived |
| **Delete** | Permanently remove (requires typing title to confirm) |

### Tech Eval Detail

Shows overview, instructor info, step list, and all sessions created from this tech eval. Stats include:
- Total students taught
- Average completion rate
- Total sessions run

---

## 11. Session Management

**Path:** Admin > Sessions

### Active Sessions Banner

When sessions are running, a green banner shows live session cards with:
- Workshop title, instructor, student count
- Elapsed time and average progress bar
- **View** and **Force End** buttons

### Session Table

| Column | Description |
|--------|-------------|
| **Name** | Session name |
| **Evaluation** | Associated tech eval |
| **Instructor** | Who created the session |
| **Status** | Scheduled / Active / Paused / Completed / Cancelled |
| **Scheduled** | When the session is planned |
| **Students** | Enrolled student count |
| **Duration** | Planned duration |

**Filters:** Search, filter by status, filter by instructor, filter by tech eval

### Actions

| Action | Description |
|--------|-------------|
| **View Detail** | Full session info with timeline, students, help requests |
| **Force End** | Immediately end an active/paused session (requires reason) |

### Session Detail

**Timeline tab** — chronological events (created, started, students joined, ended)

**Students tab** — table of enrolled students with progress bars, sorted by completion

**Help Requests tab** — all help requests for the session with priority, status, and resolution info

---

## 12. Platform Settings

**Path:** Admin > Settings

### General

| Setting | Description |
|---------|-------------|
| Platform Name | Display name for the application |
| Logo URL | Custom logo image URL |
| Default Timezone | Platform-wide default |
| Default Max Students | Default capacity for new sessions |
| Default/Max Session Duration | Duration limits (minutes) |

### Security

| Setting | Description |
|---------|-------------|
| Min Password Length | Minimum characters (6-32) |
| Require Complexity | Enforce special characters, numbers, etc. |
| Max Login Attempts | Before account lockout |
| Lockout Duration | How long the lockout lasts (minutes) |
| Session Timeout | JWT expiry (hours) |
| Allow Self-Registration | Public signup toggle |

### Email

Configure the email service provider (AWS SES or SMTP), sender address, and sender display name.

### Feature Flags

| Flag | Description |
|------|-------------|
| Demo Credentials | Show demo login on login page |
| Help Requests | Enable/disable help request system |
| Real-Time Updates | Enable/disable WebSocket features |
| Student Self-Registration | Allow students to create accounts |

---

## 13. Audit Log

**Path:** Admin > Audit Log

Tracks all significant actions performed on the platform.

### Filters

- **Date range** — start and end date
- **Action type** — Login, User Management, Tech Evals, Sessions, Settings
- **Search** — free text search in event details

### Log Columns

| Column | Description |
|--------|-------------|
| **Timestamp** | When the action occurred |
| **Action** | Color-coded label (e.g., "User Login Success", "Tech Eval Published") |
| **User** | Who performed the action (email + role) |
| **Target** | What was affected (user, tech eval, session) |
| **Details** | JSON payload (click to expand) |
| **IP Address** | Source IP |

### Tracked Actions

**User Events:** Login success/failure, registration, creation, update, role change, deactivation, reactivation, password reset, deletion, bulk import

**Tech Eval Events:** Created, published, archived, deleted, ownership transferred, status changed

**Session Events:** Created, started, ended, force ended

**Settings Events:** Settings updated

### Export

Click **Export CSV** to download the filtered audit log as a CSV file (named audit-log-YYYY-MM-DD.csv).

---

## 14. Desktop Management

**Path:** Admin > Desktops

### Summary Cards

| Card | Description |
|------|-------------|
| **Total Desktops** | Total desktop connections registered |
| **Available** | Ready to be assigned |
| **Assigned** | Currently assigned to students |
| **Active Connections** | Students actively connected (pulsing indicator) |

### Guacamole Health

Live health indicator showing:
- Connection status (Healthy / Unhealthy / Unreachable)
- Response time in milliseconds
- Server URL
- **Check** button to manually test connectivity

### Desktop Pool Table

| Column | Description |
|--------|-------------|
| **Name** | Desktop/connection name |
| **Protocol** | RDP, VNC, etc. |
| **Status** | Available / Assigned / Connected / Error |
| **Assigned To** | Student name (if assigned) |
| **Session** | Which session (clickable link) |
| **Connected Since** | How long the student has been connected |

### Active Connections

Grid of cards showing all currently connected students:
- Desktop name and student name
- Session name
- Connection duration
- **Force Disconnect** button (with confirmation dialog)

### Auto-Refresh

Toggle **Auto-refresh 30s** to keep the dashboard current. Manual refresh also available.

---

**Need help?** Contact the platform team or check the audit log for recent activity.
`;
